import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { gql, useMutation, useQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

const GET_OOP_BY_APPLICATION_ID = gql`
   query GetOOPsByApplicationId($applicationId: ID!) {
      getOOPsByApplicationId(applicationId: $applicationId) {
         _id
         OOPstatus
         officialReceipt {
            orNumber
            dateIssued
            amount
            paymentMethod
            remarks
            receiptImage
         }
      }
   }
`;

const GET_PERMIT_BY_ID = gql`
   query GetPermitById($id: ID!) {
      getPermitById(id: $id) {
         applicationType
         hasInspectionReport
      }
   }
`;

const UPDATE_PERMIT_STAGE = gql`
   mutation UpdatePermitStage(
      $id: ID!,
      $currentStage: String!,
      $status: String!,
      $notes: String,
      $approvedByTechnicalStaff: Boolean,
      $awaitingPermitCreation: Boolean,
      $ApprovedInventoryRequest: Boolean,
      $hasInspectionReport: Boolean
   ) {
      updatePermitStage(
         id: $id,
         currentStage: $currentStage,
         status: $status,
         notes: $notes,
         approvedByTechnicalStaff: $approvedByTechnicalStaff,
         awaitingPermitCreation: $awaitingPermitCreation,
         ApprovedInventoryRequest: $ApprovedInventoryRequest,
         hasInspectionReport: $hasInspectionReport
      ) {
         id
         currentStage
         status
         requestType
         approvedByTechnicalStaff
         awaitingPermitCreation
         ApprovedInventoryRequest
         hasInspectionReport
      }
   }
   `;

const GET_PLTP_PERMIT = gql`
   query GetPLTPPermit($id: ID!) {
      getPLTPPermitById(id: $id) {
         id
         applicationNumber
         applicationType
         requestType
         status
         currentStage
         hasInspectionReport
         OOPCreated
      }
   }
   `;

const GET_TCEBP_PERMIT = gql`
   query GetTCEBPPermit($id: ID!) {
      getTCEBPPermitById(id: $id) {
         id
         applicationNumber
         applicationType
         requestType
         status
         currentStage
         hasInspectionReport
         OOPCreated
      }
   }
   `;

const GET_PLTCP_PERMIT = gql`
   query GetPLTCPPermit($id: ID!) {
      getPLTCPPermitById(id: $id) {
         id
         applicationNumber
         applicationType
         requestType
         status
         currentStage
         hasInspectionReport
         OOPCreated
      }
   }
`;

const GET_COV_PERMIT = gql`
   query GetCOVPermit($id: ID!) {
      getCOVPermitById(id: $id) {
         id
         applicationNumber
         applicationType
         status
         currentStage
         hasInspectionReport
      }
   }
`;

const GET_CSAW_PERMIT = gql`
   query GetCSAWPermit($id: ID!) {
      getCSAWPermitById(id: $id) {
         id
         applicationNumber
         applicationType
         status
         currentStage
         hasInspectionReport
      }
   }
`;

const TS_AuthenticityReviewModal = ({ isOpen, onClose, application, onReviewComplete }) => {
   const { data: applicationData } = useQuery(GET_PERMIT_BY_ID, {
      variables: { id: application?.id },
      skip: !application?.id,
   });

   const [updatePermitStage] = useMutation(UPDATE_PERMIT_STAGE);
   const [checklist, setChecklist] = useState({
      documentsVerified: false,
      physicalInspection: false,
      detailsMatch: false
   });

   const { data: oopData } = useQuery(GET_OOP_BY_APPLICATION_ID, {
      variables: { applicationId: application?.id },
      skip: !application?.id,
   });

   const { data: pltpData } = useQuery(GET_PLTP_PERMIT, {
      variables: { id: application?.id },
      skip: !application?.id || application?.applicationType !== 'Private Land Timber Permit',
   });

   const { data: tcebpData } = useQuery(GET_TCEBP_PERMIT, {
      variables: { id: application?.id },
      skip: !application?.id || application?.applicationType !== 'Tree Cutting and/or Earth Balling Permit',
   });

   const { data: pltcpData } = useQuery(GET_PLTCP_PERMIT, {
      variables: { id: application?.id },
      skip: !application?.id || application?.applicationType !== 'Public Land Tree Cutting Permit',
   });

   const { data: covData } = useQuery(GET_COV_PERMIT, {
      variables: { id: application?.id },
      skip: !application?.id || application?.applicationType !== 'Certificate of Verification',
   });
   const { data: csawData } = useQuery(GET_CSAW_PERMIT, {
      variables: { id: application?.id },
      skip: !application?.id || application?.applicationType !== 'Chainsaw Registration',
   });

   useEffect(() => {
      const autoCheckEnabled = localStorage.getItem('autoCheckEnabled') === 'true';
      if (autoCheckEnabled && isOpen) {
         setChecklist({
            documentsVerified: true,
            physicalInspection: true,
            detailsMatch: true
         });
      }
   }, [isOpen]);

   const allChecked = Object.values(checklist).every(value => value === true);

   const isInventoryRequest = (
      pltpData?.getPLTPPermitById?.requestType === 'Inventory' ||
      tcebpData?.getTCEBPPermitById?.requestType === 'Inventory' ||
      pltcpData?.getPLTCPPermitById?.requestType === 'Inventory'
   );

   const isCuttingRequest = (
      pltpData?.getPLTPPermitById?.requestType === 'Cutting' ||
      tcebpData?.getTCEBPPermitById?.requestType === 'Cutting' ||
      pltcpData?.getPLTCPPermitById?.requestType === 'Cutting'
   );

   const handleApproveAuthenticity = async () => {
      try {
         if (!allChecked) {
            toast.error('Please confirm all checkboxes before approving');
            return;
         }

         if (!application || !application.id) {
            throw new Error('Invalid application data');
         }

         // Check if it's an inventory request first
         if (isInventoryRequest) {
            const hasInspectionReport = pltpData?.getPLTPPermitById?.hasInspectionReport ||
               tcebpData?.getTCEBPPermitById?.hasInspectionReport ||
               pltcpData?.getPLTCPPermitById?.hasInspectionReport;

            if (hasInspectionReport) {
               await handleInventoryRequest();
               return;
            } else {
               toast.error('Application does not have an inspection report yet!');
               return;
            }
         }

         // Get application type specific data
         const applicationType = application?.applicationType;
         const hasInspectionReport = applicationData?.getPermitById?.hasInspectionReport;
         const hasOfficialReceipt = oopData?.getOOPsByApplicationId?.some(oop =>
            oop.officialReceipt && oop.OOPstatus === 'Issued OR'
         );

         // Handle each permit type according to their flow
         switch (applicationType) {
            case 'Chainsaw Registration': // CSAW
               if (!hasOfficialReceipt) {
                  toast.error('Application does not have an Official Receipt yet!');
                  return;
               }
               if (!hasInspectionReport) {
                  toast.error('Application does not have an inspection report yet!');
                  return;
               }
               break;

            case 'Certificate of Verification': // COV
               if (!hasInspectionReport) {
                  toast.error('Application does not have an inspection report yet!');
                  return;
               }
               if (!hasOfficialReceipt) {
                  toast.error('Application does not have an Official Receipt yet!');
                  return;
               }
               break;

            case 'Private Tree Plantation Registration': // PTPR
               if (!hasInspectionReport) {
                  toast.error('Application does not have an inspection report yet!');
                  return;
               }
               break;

            case 'Public Land Tree Cutting Permit': // PLTCP
            case 'Private Land Timber Permit': // PLTP
            case 'Tree Cutting and/or Earth Balling Permit': // TCEBP
               if (isCuttingRequest) {
                  if (!hasOfficialReceipt) {
                     toast.error('Application does not have an Official Receipt yet!');
                     return;
                  }
               } else {
                  if (!hasInspectionReport) {
                     toast.error('Application does not have an inspection report yet!');
                     return;
                  }
               }
               break;

            default:
               throw new Error('Unknown application type');
         }

         // If all checks pass, approve the authenticity
         const result = await updatePermitStage({
            variables: {
               id: application.id,
               currentStage: 'AuthenticityApprovedByTechnicalStaff',
               status: 'In Progress',
               notes: 'Application authenticity approved by Technical Staff',
               approvedByTechnicalStaff: true,
               awaitingPermitCreation: true
            }
         });

         onReviewComplete();
         onClose();
         toast.success('Application approved and ready for certificate issuance');
      } catch (error) {
         console.error('Error approving authenticity:', error);
         toast.error(`Error approving authenticity: ${error.message}`);
      }
   };

   const handleInventoryRequest = async () => {
      try {
         console.log('Approving inventory request...');
         const result = await updatePermitStage({
            variables: {
               id: application.id,
               currentStage: 'ApprovedInventoryRequest',
               status: 'Released',
               notes: 'Inventory request approved by Technical Staff',
               // approvedByTechnicalStaff: true,
               awaitingPermitCreation: false,
               ApprovedInventoryRequest: true
            }
         });
         console.log('Inventory request result:', result);

         onReviewComplete();
         onClose();
         toast.success('Inventory request approved successfully');
      } catch (error) {
         console.error('Error approving inventory request:', error);
         toast.error(`Error approving inventory request: ${error.message}`);
      }
   };

   const dialogTitle = isInventoryRequest ?
      'Approve Inventory Request' :
      'Approve Application Authenticity';

   return (
      <Dialog open={isOpen} onOpenChange={onClose}>
         <DialogContent className="max-w-md">
            <DialogHeader>
               <DialogTitle>{dialogTitle}</DialogTitle>
               <DialogDescription>
                  <span>Application Number: {application?.applicationNumber}</span>
                  {isInventoryRequest && (
                     <span className="mt-2 block text-sm text-muted-foreground">
                        Type: Inventory Request
                     </span>
                  )}
               </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
               <div className="text-sm text-gray-600 dark:text-gray-300">
                  <span className="mb-2 block">By approving authenticity, you confirm that:</span>
                  <div className="space-y-3">
                     <div className="flex items-center space-x-2">
                        <Checkbox
                           id="documentsVerified"
                           checked={checklist.documentsVerified}
                           onCheckedChange={(checked) =>
                              setChecklist(prev => ({ ...prev, documentsVerified: checked }))
                           }
                        />
                        <label htmlFor="documentsVerified">All original documents have been verified</label>
                     </div>
                     <div className="flex items-center space-x-2">
                        <Checkbox
                           id="physicalInspection"
                           checked={checklist.physicalInspection}
                           onCheckedChange={(checked) =>
                              setChecklist(prev => ({ ...prev, physicalInspection: checked }))
                           }
                        />
                        <label htmlFor="physicalInspection">Physical inspection of the chainsaw has been completed</label>
                     </div>
                     <div className="flex items-center space-x-2">
                        <Checkbox
                           id="detailsMatch"
                           checked={checklist.detailsMatch}
                           onCheckedChange={(checked) =>
                              setChecklist(prev => ({ ...prev, detailsMatch: checked }))
                           }
                        />
                        <label htmlFor="detailsMatch">All details match the submitted application</label>
                     </div>
                  </div>
               </div>
               <div className="flex space-x-2">
                  <Button
                     onClick={onClose}
                     variant="outline"
                     className="flex-1"
                  >
                     Cancel
                  </Button>
                  <Button
                     onClick={handleApproveAuthenticity}
                     className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                     disabled={!allChecked}
                  >
                     Approve Authenticity
                  </Button>
               </div>
            </div>
         </DialogContent>
      </Dialog>
   );
};

export default TS_AuthenticityReviewModal;
